/**
 * v24 BOLT-118 — the landmark tag table, shared by BOTH harvests.
 *
 * FIELD BUG (Jake, 2026-07-29): every "Nearest" card was dealt DISABLED in a
 * practice round on a map that plainly has parks, food and water. Cause: the
 * harvest existed on WEB ONLY. iOS was dealt the cards (it declares protocol
 * 10) and then never uploaded a single feature, so the data requirement
 * disabled all six, forever, everywhere. The map was never the problem.
 *
 * The table lives here so the two renderers cannot describe the same card
 * differently. `landmarkTags.test.ts` asserts the Swift port matches.
 */
import { CORE_DECK } from './packs/core';

export interface LandmarkTagSpec {
  /** OpenMapTiles source-layers to read. */
  layers: readonly string[];
  /**
   * Values that identify this tag, checked against the feature's `class` and
   * `subclass`. An empty list means "every feature in these layers counts"
   * (water areas, where the layer itself IS the answer).
   */
  classes: readonly string[];
  /** Player-facing "this map has none" copy, mirrored server-side. */
  absent: string;
}

export const LANDMARK_TAG_SPECS: Record<string, LandmarkTagSpec> = {
  // `poi` is NOT optional here. Verified against the default play area's real
  // tiles: the OMT `park` source-layer is EMPTY at play zoom (it carries
  // national parks / protected areas), `landuse` carried no park class at all,
  // and every one of the 103 actual parks arrived as a `poi` point. Reading
  // only park+landuse is why Nearest Park was the one card still disabled after
  // the harvest was fixed.
  park: {
    layers: ['poi', 'park', 'landuse'],
    classes: ['park', 'nature_reserve', 'dog_park', 'garden'],
    absent: 'No parks in or near your play area',
  },
  // restaurant + fast_food + cafe merge into ONE "dining" class: each place
  // stays its own site, but all three tags contribute (BOLT-119).
  dining: {
    layers: ['poi'],
    classes: ['restaurant', 'fast_food', 'cafe'],
    absent: 'No places to eat in or near your play area',
  },
  playground: {
    layers: ['poi', 'landuse'],
    classes: ['playground'],
    absent: 'No playgrounds in or near your play area',
  },
  school: {
    layers: ['poi', 'landuse'],
    classes: ['school', 'kindergarten', 'college', 'university'],
    absent: 'No schools in or near your play area',
  },
  // natural=water (areas) + waterway lines: one "water" set of DISTINCT sites,
  // never one fused blob (BOLT-122). The `water` layer needs no class filter.
  water: {
    layers: ['water', 'waterway'],
    classes: ['river', 'stream', 'canal'],
    absent: 'No water in or near your play area',
  },
  bridge: {
    layers: ['transportation'],
    classes: [], // matched on the `brunnel` property instead
    absent: 'No bridges in or near your play area',
  },
};

export const LANDMARK_TAGS = Object.keys(LANDMARK_TAG_SPECS);

/** The whole-layer tags: every feature in the layer counts, no class filter. */
export const WHOLE_LAYER: Record<string, readonly string[]> = {
  water: ['water'], // every polygon in the `water` layer IS water
};

export function absentReasonFor(tag: string): string {
  return LANDMARK_TAG_SPECS[tag]?.absent ?? 'Not enough map features in or near your play area';
}

/**
 * Card id -> tag, DERIVED from CORE_DECK so it cannot drift from the deck.
 * (The v18 lesson: never hand-maintain a card-id list.)
 */
export const LANDMARK_TAG_FOR_CARD: Record<string, string> = Object.fromEntries(
  CORE_DECK.filter(
    (c) => c.behavior.kind === 'builtin' && c.behavior.ref === 'nearest',
  ).map((c) => [c.id, String((c.behavior as { params?: Record<string, unknown> }).params?.tag ?? '')]),
);
